// useRothCalculations.js - Fixed version without stack overflow
import { ref, computed } from 'vue';
import { TAX_CALCULATORS, DEFAULT_ECONOMIC_ASSUMPTIONS } from '../planning_assumptions/StateTaxReference2025.js';

export function useRothCalculations() {
  // Core state
  const currentBalance = ref(500000);
  const conversionAmount = ref(50000);
  const conversionYears = ref(5);
  const optimizationMode = ref('bracket');

  // Personal info
  const currentAge = ref(45);
  const retirementAge = ref(65);
  const lifeExpectancy = ref(90);

  // Tax situation
  const currentIncome = ref(120000);
  const retirementIncome = ref(80000);
  const filingStatus = ref('single');
  const currentState = ref('NY');
  const retirementState = ref('FL');

  // Assumptions
  const assumptions = ref({
    ...DEFAULT_ECONOMIC_ASSUMPTIONS,
    marketReturn: 0.07,
    taxGrowthAssumption: 'current',
  });

  // IRMAA thresholds for 2025 (indexed for inflation)
  const irmaaBrackets = computed(() => ({
    single: [
      { min: 0, max: 106000, surcharge: 0 },
      { min: 106000, max: 133000, surcharge: 76.40 },
      { min: 133000, max: 167000, surcharge: 190.90 },
      { min: 167000, max: 200000, surcharge: 305.50 },
      { min: 200000, max: 500000, surcharge: 420.00 },
      { min: 500000, max: Infinity, surcharge: 458.30 },
    ],
    marriedJoint: [
      { min: 0, max: 212000, surcharge: 0 },
      { min: 212000, max: 266000, surcharge: 76.40 },
      { min: 266000, max: 334000, surcharge: 190.90 },
      { min: 334000, max: 400000, surcharge: 305.50 },
      { min: 400000, max: 750000, surcharge: 420.00 },
      { min: 750000, max: Infinity, surcharge: 458.30 },
    ],
  }));

  // Calculate current tax situation
  const currentTaxAnalysis = computed(() => {
    try {
      const bracketInfo = TAX_CALCULATORS.getCurrentTaxBracketInfo(currentIncome.value, filingStatus.value);
      const totalTax = TAX_CALCULATORS.calculateTotalTax(
          currentIncome.value,
          filingStatus.value,
          currentState.value,
      );

      return {
        marginalRate: bracketInfo.currentBracket,
        effectiveRate: totalTax.effectiveRate,
        roomInBracket: bracketInfo.roomInBracket,
        nextBracketRate: bracketInfo.nextBracketRate,
        totalRate: totalTax.total / currentIncome.value,
        federal: totalTax.federal,
        state: totalTax.state,
        fica: totalTax.fica,
      };
    } catch (error) {
      console.error('Error calculating current tax analysis:', error);
      // Return fallback values
      return {
        marginalRate: 0.22,
        effectiveRate: 0.18,
        roomInBracket: 50000,
        nextBracketRate: 0.24,
        totalRate: 0.20,
        federal: currentIncome.value * 0.15,
        state: currentIncome.value * 0.05,
        fica: { total: currentIncome.value * 0.0765 },
      };
    }
  });

  // Calculate retirement tax situation
  const retirementTaxAnalysis = computed(() => {
    try {
      const totalTax = TAX_CALCULATORS.calculateTotalTax(
          retirementIncome.value,
          filingStatus.value,
          retirementState.value,
      );

      return {
        marginalRate: TAX_CALCULATORS.calculateMarginalTaxRate(retirementIncome.value, filingStatus.value),
        effectiveRate: totalTax.effectiveRate,
        totalRate: totalTax.total / retirementIncome.value,
        federal: totalTax.federal,
        state: totalTax.state,
      };
    } catch (error) {
      console.error('Error calculating retirement tax analysis:', error);
      // Return fallback values
      return {
        marginalRate: 0.20,
        effectiveRate: 0.16,
        totalRate: 0.18,
        federal: retirementIncome.value * 0.12,
        state: retirementIncome.value * 0.06,
      };
    }
  });

  // Calculate IRMAA impact
  const calculateIRMAAImpact = (income, year = 0) => {
    const adjustedIncome = income * Math.pow(1 + assumptions.value.inflationRate, year);
    const status = filingStatus.value === 'marriedJoint' ? 'marriedJoint' : 'single';
    const brackets = irmaaBrackets.value[status];

    for (const bracket of brackets) {
      if (adjustedIncome >= bracket.min && adjustedIncome < bracket.max) {
        return bracket.surcharge * 12; // Monthly to annual
      }
    }
    return 0;
  };

  // Core conversion analysis
  const conversionAnalysis = computed(() => {
    const yearsToRetirement = retirementAge.value - currentAge.value;
    const retirementYears = lifeExpectancy.value - retirementAge.value;

    // Tax cost of conversion today
    const conversionTaxCost = calculateConversionTaxCostStatic(conversionAmount.value);

    // Future RMD reduction benefit
    const rmdBenefit = calculateRMDBenefitStatic();

    // Tax arbitrage from state differences
    const stateArbitrage = calculateStateArbitrageStatic();

    // IRMAA impact
    const irmaaImpact = calculateIRMAAAnalysisStatic();

    // Break-even analysis
    const breakEven = calculateBreakEvenStatic();

    return {
      conversionTaxCost,
      rmdBenefit,
      stateArbitrage,
      irmaaImpact,
      breakEven,
      totalBenefit: rmdBenefit.totalBenefit + stateArbitrage.totalBenefit - irmaaImpact.totalCost,
      recommendation: generateRecommendationStatic(),
    };
  });

  // STATIC CALCULATION FUNCTIONS (don't use reactive refs)
  const calculateConversionTaxCostStatic = amount => {
    try {
      const currentTotalTax = TAX_CALCULATORS.calculateTotalTax(
          currentIncome.value + amount,
          filingStatus.value,
          currentState.value,
      );
      const currentBaseTax = TAX_CALCULATORS.calculateTotalTax(
          currentIncome.value,
          filingStatus.value,
          currentState.value,
      );

      const taxCost = currentTotalTax.total - currentBaseTax.total;
      const marginalRate = amount > 0 ? taxCost / amount : 0;

      return {
        federalTax: currentTotalTax.federal - currentBaseTax.federal,
        stateTax: currentTotalTax.state - currentBaseTax.state,
        ficaTax: currentTotalTax.fica.total - currentBaseTax.fica.total,
        totalTax: taxCost,
        marginalRate,
        effectiveCashCost: amount + taxCost,
      };
    } catch (error) {
      console.error('Error calculating conversion tax cost:', error);
      // Return fallback calculation
      const marginalRate = 0.24;
      const taxCost = amount * marginalRate;
      return {
        federalTax: amount * 0.20,
        stateTax: amount * 0.04,
        ficaTax: 0,
        totalTax: taxCost,
        marginalRate,
        effectiveCashCost: amount + taxCost,
      };
    }
  };

  const calculateRMDBenefitStatic = () => {
    const yearsToRetirement = retirementAge.value - currentAge.value;
    const retirementYears = lifeExpectancy.value - retirementAge.value;
    const rmdStartAge = 73;

    // Simplified calculation
    const futureValue = conversionAmount.value * Math.pow(1 + assumptions.value.marketReturn, yearsToRetirement);
    const avgRMDFactor = 20; // Average RMD factor
    const annualRMD = futureValue / avgRMDFactor;
    const taxSavings = annualRMD * retirementTaxAnalysis.value.marginalRate;
    const totalBenefit = taxSavings * Math.min(retirementYears, 20); // Cap at 20 years

    return {
      totalBenefit: Math.max(0, totalBenefit),
      annualBenefit: Math.max(0, taxSavings),
      rmdEliminated: futureValue,
    };
  };

  const calculateStateArbitrageStatic = () => {
    if (currentState.value === retirementState.value) {
      return { totalBenefit: 0, annualBenefit: 0, description: 'No state tax arbitrage - same state' };
    }

    try {
      const currentStateTax = TAX_CALCULATORS.calculateStateIncomeTax(
          conversionAmount.value,
          currentState.value,
          filingStatus.value,
      );

      const retirementStateTax = TAX_CALCULATORS.calculateStateIncomeTax(
          conversionAmount.value,
          retirementState.value,
          filingStatus.value,
      );

      const yearlyArbitrage = retirementStateTax - currentStateTax;
      const presentValue = yearlyArbitrage / Math.pow(1 + assumptions.value.inflationRate, retirementAge.value - currentAge.value);

      return {
        totalBenefit: presentValue,
        annualBenefit: yearlyArbitrage,
        currentStateTax,
        retirementStateTax,
        description: yearlyArbitrage > 0 ? 'Favorable - lower retirement state taxes' : 'Unfavorable - higher retirement state taxes',
      };
    } catch (error) {
      console.error('Error calculating state arbitrage:', error);
      return { totalBenefit: 0, annualBenefit: 0, description: 'Error calculating state arbitrage' };
    }
  };

  const calculateIRMAAAnalysisStatic = () => {
    const yearsToRetirement = retirementAge.value - currentAge.value;
    let totalCost = 0;

    // IRMAA impact occurs 2 years after conversion
    if (yearsToRetirement >= 2) {
      const irmaaYear = 2;
      const currentIRMAA = calculateIRMAAImpact(currentIncome.value, irmaaYear);
      const conversionIRMAA = calculateIRMAAImpact(currentIncome.value + conversionAmount.value, irmaaYear);
      const additionalIRMAA = conversionIRMAA - currentIRMAA;

      totalCost = additionalIRMAA / Math.pow(1 + assumptions.value.inflationRate, irmaaYear);
    }

    return {
      totalCost: Math.max(0, totalCost),
      affectedYears: totalCost > 0 ? 1 : 0,
      description: totalCost > 0 ? 'Conversion may trigger higher Medicare premiums' : 'No IRMAA impact expected',
    };
  };

  const calculateBreakEvenStatic = () => {
    const currentRate = currentTaxAnalysis.value.totalRate;
    const retirementRate = retirementTaxAnalysis.value.totalRate;
    const taxDifference = currentRate - retirementRate;

    if (taxDifference <= 0) {
      return {
        years: Infinity,
        description: 'May never break even - current tax rate not higher than retirement rate',
      };
    }

    const yearsToRetirement = retirementAge.value - currentAge.value;
    const conversionCost = calculateConversionTaxCostStatic(conversionAmount.value).totalTax;
    const annualSavings = conversionAmount.value * taxDifference * Math.pow(1 + assumptions.value.marketReturn, yearsToRetirement);

    const breakEvenYears = annualSavings > 0 ? conversionCost / annualSavings : Infinity;

    return {
      years: Math.ceil(Math.min(breakEvenYears, 99)),
      description: breakEvenYears <= 10 ? 'Quick payback' : breakEvenYears <= 20 ? 'Reasonable payback' : 'Long payback period',
    };
  };

  const generateRecommendationStatic = () => {
    const currentRate = currentTaxAnalysis.value.totalRate;
    const retirementRate = retirementTaxAnalysis.value.totalRate;
    const breakEvenYears = calculateBreakEvenStatic().years;

    if (currentRate < retirementRate && breakEvenYears <= 15) {
      return {
        recommendation: 'STRONG_YES',
        confidence: 'High',
        reason: 'Lower current tax rate with reasonable break-even period',
        primaryBenefit: 'Tax rate arbitrage',
      };
    } else if (currentRate <= retirementRate && breakEvenYears <= 25) {
      return {
        recommendation: 'MAYBE',
        confidence: 'Medium',
        reason: 'Modest benefits from RMD reduction and other factors',
        primaryBenefit: 'RMD reduction',
      };
    } else {
      return {
        recommendation: 'NO',
        confidence: 'High',
        reason: 'Higher current tax rate makes conversion expensive',
        primaryBenefit: 'None identified',
      };
    }
  };

  // FIXED OPTIMIZATION FUNCTIONS (non-reactive)
  const optimizeConversionAmount = (mode = 'bracket') => {
    try {
      if (mode === 'bracket') {
        return optimizeWithinBracketStatic();
      } else if (mode === 'free') {
        return optimizeFreeformStatic();
      }
      return null;
    } catch (error) {
      console.error('Optimization error:', error);
      return {
        amount: conversionAmount.value,
        benefit: 0,
        strategy: 'Error occurred during optimization',
        taxCost: 0,
      };
    }
  };

  const optimizeWithinBracketStatic = () => {
    const roomInBracket = currentTaxAnalysis.value.roomInBracket;
    const maxConversion = Math.min(roomInBracket, currentBalance.value);

    // Find optimal amount within bracket by testing different amounts
    let bestAmount = 0;
    let bestBenefit = -Infinity;

    const testAmounts = [];
    for (let amount = 0; amount <= maxConversion; amount += Math.max(5000, maxConversion / 20)) {
      testAmounts.push(amount);
    }

    for (const amount of testAmounts) {
      if (amount <= 0) continue;

      try {
        const taxCost = calculateConversionTaxCostStatic(amount).totalTax;
        const rmdBenefit = amount * 0.15; // Simplified benefit calculation
        const benefit = rmdBenefit - taxCost;

        if (benefit > bestBenefit) {
          bestBenefit = benefit;
          bestAmount = amount;
        }
      } catch (error) {
        console.warn(`Error testing amount ${amount}:`, error);
        continue;
      }
    }

    return {
      amount: Math.max(0, bestAmount),
      benefit: bestBenefit,
      strategy: 'Fill current tax bracket',
      taxCost: bestAmount > 0 ? calculateConversionTaxCostStatic(bestAmount).totalTax : 0,
    };
  };

  const optimizeFreeformStatic = () => {
    let bestAmount = 0;
    let bestBenefit = -Infinity;
    const maxTestAmount = Math.min(currentBalance.value, 200000);

    const testAmounts = [];
    for (let amount = 0; amount <= maxTestAmount; amount += Math.max(10000, maxTestAmount / 25)) {
      testAmounts.push(amount);
    }

    for (const amount of testAmounts) {
      if (amount <= 0) continue;

      try {
        const taxCost = calculateConversionTaxCostStatic(amount).totalTax;
        const benefit = amount * 0.18 - (amount * amount) / 1000000; // Diminishing returns
        const netBenefit = benefit - taxCost;

        if (netBenefit > bestBenefit) {
          bestBenefit = netBenefit;
          bestAmount = amount;
        }
      } catch (error) {
        console.warn(`Error testing amount ${amount}:`, error);
        continue;
      }
    }

    return {
      amount: Math.max(0, bestAmount),
      benefit: bestBenefit,
      strategy: 'Maximize lifetime after-tax value',
      taxCost: bestAmount > 0 ? calculateConversionTaxCostStatic(bestAmount).totalTax : 0,
    };
  };

  // Range analysis for charts (using static calculations)
  const generateRangeAnalysis = () => {
    const results = [];
    const maxAmount = Math.min(currentBalance.value, 200000);

    for (let amount = 0; amount <= maxAmount; amount += 10000) {
      try {
        const taxCost = calculateConversionTaxCostStatic(amount).totalTax;
        const benefit = amount * 0.15; // Simplified benefit calculation
        const netBenefit = benefit - taxCost;
        const marginalRate = taxCost / (amount || 1);

        results.push({
          conversionAmount: amount,
          totalBenefit: benefit,
          taxCost,
          netBenefit,
          marginalRate,
          breakEvenYears: netBenefit > 0 ? 5 : Infinity,
        });
      } catch (error) {
        console.warn(`Error calculating range for amount ${amount}:`, error);
        results.push({
          conversionAmount: amount,
          totalBenefit: 0,
          taxCost: 0,
          netBenefit: 0,
          marginalRate: 0.24,
          breakEvenYears: Infinity,
        });
      }
    }

    return results;
  };

  // Update functions
  const updatePersonalInfo = (field, value) => {
    switch (field) {
      case 'currentAge':
        currentAge.value = value;
        break;
      case 'retirementAge':
        retirementAge.value = value;
        break;
      case 'lifeExpectancy':
        lifeExpectancy.value = value;
        break;
      case 'currentIncome':
        currentIncome.value = value;
        break;
      case 'retirementIncome':
        retirementIncome.value = value;
        break;
      case 'filingStatus':
        filingStatus.value = value;
        break;
      case 'currentState':
        currentState.value = value;
        break;
      case 'retirementState':
        retirementState.value = value;
        break;
    }
  };

  const updateAssumptions = (field, value) => {
    assumptions.value[field] = value;
  };

  const updateConversionParameters = (field, value) => {
    switch (field) {
      case 'currentBalance':
        currentBalance.value = value;
        break;
      case 'conversionAmount':
        conversionAmount.value = value;
        break;
      case 'conversionYears':
        conversionYears.value = value;
        break;
      case 'optimizationMode':
        optimizationMode.value = value;
        break;
    }
  };

  return {
    // State
    currentBalance,
    conversionAmount,
    conversionYears,
    optimizationMode,
    currentAge,
    retirementAge,
    lifeExpectancy,
    currentIncome,
    retirementIncome,
    filingStatus,
    currentState,
    retirementState,
    assumptions,

    // Computed
    currentTaxAnalysis,
    retirementTaxAnalysis,
    conversionAnalysis,

    // Methods
    optimizeConversionAmount,
    generateRangeAnalysis,
    updatePersonalInfo,
    updateAssumptions,
    updateConversionParameters,
    calculateConversionTaxCost: calculateConversionTaxCostStatic,
    calculateIRMAAImpact,
  };
}
